import { SafePromise, SafeError, safeMap } from '@benzinga/safe-await';
import { CompareProductTableProps } from '@benzinga/product-ui';
import {
  AltOffering,
  AltOfferingCompany,
  AltOfferingProduct,
  AssetType,
  OfferingAttribute,
  PaginatedAltOffering,
} from './entities';

export interface PaginatedIngressAltOffering {
  offerings: IngressAltOffering[];
  total: number;
}
export interface IngressAltOffering {
  affiliate_link: string;
  asset_type: AssetType;
  attributes: OfferingAttribute[];
  company_id: number;
  company: IngressAltOfferingCompany;
  created_at: string;
  deleted_at: string;
  featured: boolean;
  funded: number;
  id: number;
  image: string;
  min_investment: number;
  name: string;
  non_accredited: boolean;
  platform: string;
  priority: number;
  product_id: number;
  product: IngressAltOfferingProduct;
  projected_return_high: number;
  projected_return_low: number;
  property_type: string;
  securities_offered: string;
  slug: string;
  term_high: number;
  term_low: number;
  type: string;
  updated_at: string;
  product_block?: CompareProductTableProps | null;
}

export interface IngressAltOfferingCompany {
  affiliate_link_company: string;
  available_to: string;
  description: string;
  email: string;
  hide_competitors: boolean;
  historic_returns: string;
  id: number;
  image_wide: string;
  image: string;
  investment_types: string[];
  investor_fees: string;
  minimum_investment: string;
  name: string;
  num_offerings: string;
  news_rss_feed_link: string;
  offerings?: IngressAltOffering[];
  previous_offerings?: IngressAltOffering[];
  similar_offerings?: IngressAltOffering[];
  phone_format: string;
  phone: string;
  property_types_available: string;
  slug: string;
  company_disclosure?: string;
}

export interface IngressAltOfferingProduct {
  affiliate_link: string;
  home_offer_type: string;
  id: number;
  image: string;
  investment_strategy: string;
  name: string;
  offering_disclosure?: string;
  offer_investment_term: string;
  offer_is_featured: boolean;
  offer_minimum_investment: string;
  offer_offering_link: string;
  offer_open_to_non_accredited: string;
  offer_platform: string;
  offer_projected_return: string;
  offering_amount: string;
  offering_close_date: string;
  offering_launch_date: string;
  product_article: string;
  product_take: string;
  profile_image: string;
  property_location: string;
  property_type: string;
  securities_offered: string;
  short_description: string;
  sponsor: string;
  target_cash_yield_high: string;
  target_cash_yield_low: string;
  target_equity_multiple: string;
  product_block?: CompareProductTableProps | null;
}

export const ingestAltOffering = async (
  offering: SafePromise<{ data: IngressAltOffering }>,
): SafePromise<AltOffering> => {
  const response = await offering;

  if (response.err) {
    return { err: response.err };
  } else {
    if (response.ok && response.ok.data) {
      const result = response.ok.data;
      return {
        ok: ingressAltOffering(result),
      };
    } else {
      return { err: new SafeError('did not get a valid offerings response', 'invalid_offering') };
    }
  }
};

export const ingestAltOfferings = (ingress: SafePromise<{ data: IngressAltOffering[] }>): SafePromise<AltOffering[]> =>
  safeMap(ingress, ingress => {
    return ingress.data.map(ingress => {
      return ingressAltOffering(ingress);
    });
  });

export const PaginatedingestAltOfferings = (
  ingress: SafePromise<{ data: PaginatedIngressAltOffering }>,
): SafePromise<PaginatedAltOffering> => {
  let total = 0;
  return safeMap(ingress, ingress => {
    total = ingress.data?.total ?? 0;
    const map = ingress.data?.offerings.map(ingress => {
      return ingressAltOffering(ingress);
    });
    return {
      offerings: map,
      total: total,
    };
  });
};

export const ingestAltOfferingCompany = async (
  offeringCompany: SafePromise<IngressAltOfferingCompany>,
): SafePromise<AltOfferingCompany> => {
  const response = await offeringCompany;

  if (response.err) {
    return { err: response.err };
  } else {
    if (response.ok) {
      return {
        ok: ingressAltOfferingCompany(response.ok),
      };
    } else {
      return { err: new SafeError('did not get a valid offering company response', 'invalid_offering_company') };
    }
  }
};

export const ingressAltOffering = (offering: IngressAltOffering): AltOffering => {
  return {
    affiliateLink: offering.affiliate_link ?? null,
    assetType: offering.asset_type ?? null,
    attributes: offering.attributes ?? null,
    company: (offering.company && ingressAltOfferingCompany(offering.company)) ?? null,
    companyId: offering.company_id ?? null,
    createdAt: offering.created_at ?? null,
    deletedAt: offering.deleted_at ?? null,
    featured: offering.featured ?? null,
    funded: offering.funded ?? null,
    id: offering.id ?? null,
    image: offering.image ?? null,
    minInvestment: offering.min_investment ?? null,
    name: offering.name ?? null,
    nonAccredited: offering.non_accredited ?? null,
    platform: offering.platform ?? null,
    priority: offering.priority ?? null,
    product: (offering.product && ingressAltOfferingProduct(offering.product)) ?? null,
    productBlock: offering?.product_block ?? null,
    productId: offering.product_id ?? null,
    projectedReturnHigh: offering.projected_return_high ?? null,
    projectedReturnLow: offering.projected_return_low ?? null,
    propertyType: offering.property_type ?? null,
    securitiesOffered: offering.securities_offered ?? null,
    slug: offering?.slug ?? null,
    termHigh: offering.term_high ?? null,
    termLow: offering.term_low ?? null,
    type: offering.type ?? null,
    updatedAt: offering.updated_at ?? null,
  };
};

export const ingressAltOfferings = (offerings: IngressAltOffering[]): AltOffering[] => {
  if (!offerings) return [];
  return offerings?.map(offering => {
    return ingressAltOffering(offering);
  });
};

export const ingressAltOfferingCompany = (offeringCompany: IngressAltOfferingCompany): AltOfferingCompany => {
  return {
    affiliateLinkCompany: offeringCompany.affiliate_link_company ?? null,
    availableTo: offeringCompany.available_to ?? null,
    companyDisclosure: offeringCompany.company_disclosure ?? null,
    description: offeringCompany.description ?? null,
    email: offeringCompany.email ?? null,
    hideCompetitors: offeringCompany.hide_competitors ?? false,
    historicReturns: offeringCompany.historic_returns ?? null,
    id: offeringCompany.id ?? null,
    image: offeringCompany.image ?? null,
    imageWide: offeringCompany.image_wide ?? null,
    investmentTypes: offeringCompany.investment_types ?? null,
    investorFees: offeringCompany.investor_fees ?? null,
    minimumInvestment: offeringCompany.minimum_investment ?? null,
    name: offeringCompany.name ?? null,
    newsRssFeedLink: offeringCompany.news_rss_feed_link ?? null,
    numOfferings: offeringCompany.num_offerings || offeringCompany?.offerings?.length || null,
    offerings: offeringCompany.offerings ? ingressAltOfferings(offeringCompany.offerings) : [],
    phone: offeringCompany.phone ?? null,
    phoneFormat: offeringCompany.phone_format ?? null,
    previousOfferings: offeringCompany.previous_offerings
      ? ingressAltOfferings(offeringCompany.previous_offerings)
      : [],
    propertyTypesAvailable: offeringCompany.property_types_available ?? null,
    similarOfferings: offeringCompany.similar_offerings ? ingressAltOfferings(offeringCompany.similar_offerings) : [],
    slug: offeringCompany?.slug ?? null,
  };
};

export const ingressAltOfferingProduct = (product: IngressAltOfferingProduct): AltOfferingProduct => {
  return {
    affiliateLink: (product.offer_offering_link || product.affiliate_link) ?? null,
    homeOfferType: product.home_offer_type ?? null,
    id: product.id ?? null,
    image: product.image ?? null,
    investmentStrategy: product.investment_strategy ?? null,
    investmentTerm: product.offer_investment_term ?? null,
    isFeatured: product.offer_is_featured ?? null,
    minInvestment: product.offer_minimum_investment ?? null,
    name: product.name ?? null,
    offeringAmount: product.offering_amount ?? null,
    offeringCloseDate: product.offering_close_date ?? null,
    offeringDisclosure: product?.offering_disclosure ?? null,
    offeringLaunchDate: product.offering_launch_date ?? null,
    openToNonAccredited: product.offer_open_to_non_accredited ?? null,
    platform: product.offer_platform ?? null,
    productArticle: product.product_article ?? null,
    productBlock: product?.product_block ?? null,
    productTake: product.product_take ?? null,
    profileImage: product.profile_image ?? null,
    projectedReturn: product.offer_projected_return ?? null,
    propertyLocation: product.property_location ?? null,
    propertyType: product.property_type ?? null,
    securitiesOffered: product.securities_offered ?? null,
    shortDescription: product.short_description ?? null,
    sponsor: product.sponsor ?? null,
    targetCashYieldHigh: product.target_cash_yield_high ?? null,
    targetCashYieldLow: product.target_cash_yield_low ?? null,
    targetEquityMultiple: product.target_equity_multiple ?? null,
  };
};

export const ingressAltOfferingProducts = (products: IngressAltOfferingProduct[]): AltOfferingProduct[] => {
  if (!products) return [];
  return products.map(product => {
    return ingressAltOfferingProduct(product);
  });
};
